// src/components/songEdit/components/composer/SearchableInput.tsx
import React, { useState, useEffect } from 'react';
import { Edit, UserPlus } from 'lucide-react';
import { InputWithIcon } from '../inputs/InputWithIcon';
import { Person, SearchResult } from '../types/composerTypes';
import { SearchDropdown } from './SearchDropdown';
import { useComposerSearch } from '../hooks/useComposerSearch';

interface SearchableInputProps {
  type: 'composer' | 'lyricist';
  value: Person | null;
  onSelect: (person: Person | null) => void;
  onAddNew: () => void;
  onEdit?: () => void;
}

export const SearchableInput: React.FC<SearchableInputProps> = ({
  type,
  value,
  onSelect,
  onAddNew,
  onEdit
}) => {
  const [inputValue, setInputValue] = useState('');
  
  useEffect(() => {
    setInputValue(value?.full_name || '');
  }, [value]);

  const {
    results,
    isLoading,
    showResults,
    setShowResults,
    handleSearch
  } = useComposerSearch(type);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (!newValue) {
      onSelect(null);
    }
    handleSearch(newValue);
  };

  const handleSelect = (result: SearchResult) => {
    const person: Person = {
      composer_id: result.composer_id,
      full_name: result.full_name,
      type: result.type || type, // Use the result type if available, otherwise use the prop type
      era: result.era,
      origin: result.origin
    };
    onSelect(person);
    setShowResults(false);
  };

  return (
    <div className="position-relative">
      <InputWithIcon
        label={type === 'composer' ? 'Composer' : 'Lyricist'}
        name={type}
        value={inputValue}
        onChange={handleInputChange}
        icon={UserPlus}
        onIconClick={onAddNew}
        rightIcon={value?.composer_id ? Edit : undefined}
        onRightIconClick={onEdit}
        placeholder={`Search ${type}...`}
      />
      <SearchDropdown
        results={results}
        isLoading={isLoading}
        onSelect={handleSelect}
        show={showResults}
      />
    </div>
  );
};